// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';
import { getProductVariantFields } from '../../util/productVariants';
import { isPurchaseProcessAlias } from '../../transactions/transaction';
import { getListingEcolabels } from '../../config/ecolabels';
import { useValuePriorities } from '../../hooks/useValuePriorities';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';
import AddToCartButton from '../AddToCartButton/AddToCartButton';
import EcolabelList from '../EcolabelList/EcolabelList';
import ValuePrioritiesMatch from '../ValuePriorities/ValuePrioritiesMatch';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

// How many ecolabel chips a card shows in its single row. A product with more
// than this many ecolabels shows the first three and a "+N" chip.
const ECOLABELS_ON_CARD = 4;

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;
  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle, transactionProcessAlias } = publicData || {};
  const ecolabels = getListingEcolabels(publicData);
  const priorities = useValuePriorities();
  // Add-to-basket only makes sense for purchase-process listings with a price.
  // Bookings, inquiries and negotiations go through their own listing-page flow.
  const hasPrice = !!listing?.attributes?.price;
  // A product sold in several sizes or colours can't go straight into the basket
  // from a card: there is nowhere to choose one, and the brand has to know what
  // to ship. Its card still shows the button, but the button takes the shopper
  // to the product page, where the choice is made and the product added.
  const hasProductVariants =
    getProductVariantFields({
      publicData,
      metadata: listing?.attributes?.metadata,
      listingFieldConfigs: config.listing.listingFields,
      categoryConfiguration: config.categoryConfiguration,
    }).length > 0;
  const showAddToCart = hasPrice && isPurchaseProcessAlias(transactionProcessAlias);
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          aspectRatioClassName={aspectRatioClassName}
          lazyLoadImage={lazyLoadImage}
        />
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          className={aspectRatioClassName}
          width={aspectWidth}
          height={aspectHeight}
          setActivePropsMaybe={setActivePropsMaybe}
        />
      )}
      <div className={css.info}>
        {/* Brand, then product, then price — the order shoppers scan a
            product in. */}
        <div className={css.mainInfo}>
          {showAuthorInfo ? (
            <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
              {authorName}
            </div>
          ) : null}
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}
        </div>
        {/* The price line is a slot rather than a conditional row: a listing
            type that hides its price leaves it empty, so such a card still
            lines up with the ones beside it. */}
        <div className={css.price} title={showPrice ? priceTooltip : undefined}>
          {showPrice ? priceMessage : null}
        </div>
        {/* The row is a fixed-height slot: it shows at most four chips (the
            fourth being "+N" when there are more), and it keeps its height even
            for a product with no ecolabels, so cards in a grid line up. */}
        <div className={css.ecolabels}>
          <EcolabelList
            ecolabels={ecolabels}
            size="small"
            limit={ECOLABELS_ON_CARD}
            highlightKeys={priorities}
          />
        </div>
        {/* Once the shopper has set priorities every card gets this line (empty
            when nothing matches), so cards in a grid still line up. */}
        {priorities.length > 0 ? (
          <div className={css.priorityMatch}>
            <ValuePrioritiesMatch
              priorities={priorities}
              ecolabelKeys={ecolabels.map(ecolabel => ecolabel.key)}
            />
          </div>
        ) : null}
        {showAddToCart ? (
          <div className={css.cta}>
            <AddToCartButton
              listing={listing}
              className={css.addToCart}
              variant="card"
              opensProductPage={hasProductVariants}
            />
          </div>
        ) : null}
      </div>
    </NamedLink>
  );
};

export default ListingCard;
